// RigSentinel · PWA install-prompt lifecycle hook.
//
// Standard `beforeinstallprompt` handling (Chrome/Edge/Android/desktop):
//   - capture + preventDefault the browser's native event so WE control
//     when the prompt fires (only after an explicit user tap, per spec).
//   - store the deferred event; call `.prompt()` on it from a user gesture.
//   - `appinstalled` fires once the user accepts — flips to installed.
//
// iOS Safari never fires `beforeinstallprompt` (no programmatic install),
// so on iOS we instead expose `showIOSInstructions` for a manual
// "Add to Home Screen" walkthrough.
//
// Standalone/installed detection covers both the standard
// `display-mode: standalone` media query and the iOS-specific
// `navigator.standalone` flag.
//
// "Don't nag" — dismissing the install UI persists a timestamp in
// localStorage; the UI stays hidden for a cooldown window afterward.
//
// SHARED SINGLETON STATE — the install surface is now reachable from
// several independently-mounted places (public entrance screen,
// /download, Settings). A browser only fires `beforeinstallprompt` once
// per page load; if it fires while, say, only Settings is mounted, a
// per-instance listener would mean /download (mounted later, after
// navigating there) never sees it. The event + installed flag are
// therefore captured ONCE at module scope (this module is a singleton —
// every import shares the same closure) and every hook instance just
// subscribes to be notified when that shared state changes. Same public
// API as before; no consumer needs to change.

import { useEffect, useState, useCallback } from "react";

const DISMISS_KEY = "rsc.pwaInstallDismissedAt.v1";
const DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

export function detectStandalone() {
  if (typeof window === "undefined") return false;
  const mq = !!(window.matchMedia && window.matchMedia("(display-mode: standalone)").matches);
  const iosStandalone = typeof window.navigator !== "undefined" && window.navigator.standalone === true;
  const androidTwa = typeof document !== "undefined" && !!document.referrer && document.referrer.startsWith("android-app://");
  return mq || iosStandalone || androidTwa;
}

export function detectIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || navigator.vendor || "";
  // iPadOS 13+ reports as "MacIntel" — the touch-points check is the
  // standard way to tell it apart from a real Mac.
  const isIPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return /iPad|iPhone|iPod/.test(ua) || isIPadOS;
}

function readDismissedRecently() {
  try {
    const t = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return t > 0 && Date.now() - t < DISMISS_COOLDOWN_MS;
  } catch {
    return false;
  }
}

// --- module-level singleton state -------------------------------------
let _deferredEvent = null;
let _installed = detectStandalone();
const _isIOS = detectIOS();
const _subscribers = new Set();

function _emit() {
  _subscribers.forEach((fn) => fn());
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault(); // we decide when to show the prompt, not the browser
    _deferredEvent = e;
    _emit();
  });
  window.addEventListener("appinstalled", () => {
    _installed = true;
    _deferredEvent = null;
    try { localStorage.removeItem(DISMISS_KEY); } catch { /* ignore */ }
    _emit();
  });
}
// ------------------------------------------------------------------------

export function usePwaInstall() {
  // Local re-render trigger — actual state lives in the module singleton
  // above so every hook instance reads the same, always-current values.
  const [, forceRender] = useState(0);
  const [dismissedRecently, setDismissedRecently] = useState(readDismissedRecently);

  useEffect(() => {
    const onChange = () => forceRender((n) => n + 1);
    _subscribers.add(onChange);
    return () => _subscribers.delete(onChange);
  }, []);

  const canPromptInstall = !!_deferredEvent && !_installed;
  const showIOSInstructions = _isIOS && !_installed && !_deferredEvent;

  // Only ever called from a user click handler (Install button) — browsers
  // require `.prompt()` to run inside a user-gesture call stack.
  const promptInstall = useCallback(async () => {
    if (!_deferredEvent) return null;
    _deferredEvent.prompt();
    try {
      const choice = await _deferredEvent.userChoice;
      if (choice.outcome === "accepted") _installed = true;
      _deferredEvent = null;
      _emit();
      return choice.outcome;
    } catch {
      _deferredEvent = null;
      _emit();
      return null;
    }
  }, []);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setDismissedRecently(true);
  }, []);

  return { installed: _installed, isIOS: _isIOS, canPromptInstall, showIOSInstructions, dismissedRecently, promptInstall, dismiss };
}
